import { fabric } from '../../../lib/fabricCompat';
import { TemplateObject } from '../BitmapEditor.types';

export const drawImage = async (
  canvas: fabric.Canvas,
  object: TemplateObject,
  selectImageHandler: any,
  sideContainer: string
) => {
  if (object.src?.includes('{{{')) {
    drawPlaceholder(canvas, object, selectImageHandler, sideContainer);
  } else {
    drawImageElement(canvas, object, selectImageHandler);
  }
};

export const drawImageElement = (
  canvas: fabric.Canvas,
  object: any,
  selectImageHandler: any
): void => {
  fabric.Image.fromURL(
    object.src,
    (img: any) => {
      img.set({
        id: object.id,
        left: object.x,
        top: object.y,
        scaleX: object.width / img.width,
        scaleY: object.height / img.height,
        hasControls: false,
        hasBorders: true,
        selectable: true,
        lockMovementX: true,
        lockMovementY: true,
        strokeWidth: 0,
        hoverCursor: 'pointer',
      });
      canvas.add(img);
      img.on('mouseover', () => {
        img.set('stroke', '#3355ff');
        canvas.renderAll();
      });
      img.on('mouseout', () => {
        img.set('stroke', 'transparent');
        canvas.renderAll();
      });
    },
    { crossOrigin: 'anonymous' }
  );
};

const drawPlaceholder = (
  canvas: fabric.Canvas,
  object: TemplateObject,
  selectImageHandler: any,
  // sideContainer no longer used after dropping the HTML button overlay
  // (KL-13820) — kept in the signature so callers don't have to change.
  // eslint-disable-next-line no-unused-vars
  sideContainer: string
) => {
  const placeHolder = createPlaceholder(canvas, object);
  const fileInput = createFileInput(canvas, object, selectImageHandler);

  fabric.loadSVGFromURL('/img/photo-editor/placeholder.svg', (objects: fabric.Object[], options: any) => {
    const svg = fabric.util.groupSVGElements(objects, options);
    const objectWidth = object.width ?? 100;
    const objectHeight = object.height ?? 100;

    const svgWidth = 100;
    const svgHeight = 100;

    const scale = Math.min(objectWidth / svgWidth, objectHeight / svgHeight);

    svg.set({
      top: object.y + objectHeight / 2 - (svgHeight * scale) / 10,
      left: object.x + objectWidth / 2 - (svgWidth * scale) / 10,
      scaleX: scale,
      scaleY: scale,
      evented: false,
      originX: 'left',
      originY: 'top',
    });
    canvas.add(svg);
  });

  // Desired hover border thickness in *displayed* pixels. The canvas is
  // backed by a print-resolution buffer (typically 1500-3000 px wide) but
  // rendered at ~30-40% CSS size via canvas.setZoom(...). A hard-coded
  // strokeWidth in fabric coords therefore shrinks/grows with the zoom.
  // Divide by getZoom() so the rendered stroke stays the same number of
  // CSS pixels on screen regardless of template size or window resize.
  // (Same pattern used by LimitedIText.hoverRect, line 96 of TextObject.)
  const HOVER_STROKE_DISPLAY_PX = 4;
  placeHolder.on('mouseover', () => {
    placeHolder.set(
      'strokeWidth',
      HOVER_STROKE_DISPLAY_PX / (canvas.getZoom() || 1)
    );
    canvas.renderAll();
  });
  placeHolder.on('mouseout', () => {
    placeHolder.set('strokeWidth', 0);
    canvas.renderAll();
  });

  // KL-13820: ditched the old absolute-positioned <button class="select-
  // image-btn"> overlay that was sized via placeHolder.getBoundingRect().
  // Bounding-rect coords are in canvas-internal pixel space (e.g. 1500×2000
  // for a print-resolution template) but were being applied as CSS pixels
  // on the button, producing a button several times larger than the visible
  // canvas. With fabric v7's reliable object event handling we can trigger
  // the file picker directly from the placeholder rect's mousedown — same
  // user-gesture context as a real button, so input.click() is allowed to
  // open the native file dialog.
  placeHolder.on('mousedown', () => {
    fileInput.click();
  });
};

const createPlaceholder = (canvas: fabric.Canvas, object: TemplateObject) => {
  const placeHolder = new fabric.Rect({
    left: object.x,
    top: object.y,
    width: object.width,
    height: object.height,
    fill: '#FCF4ED',
    selectable: true,
    hasControls: false,
    lockMovementX: true,
    lockMovementY: true,
    borderScaleFactor: 3,
    // KL-13820: was '#3355ff' (blue) but the actual hover color users
    // saw came from the now-removed HTML overlay button which used
    // 2px solid #18b2bb. Aligning the rect's stroke to match so the
    // hover state looks the same as before the overlay removal.
    stroke: '#18b2bb',
    strokeWidth: 0,
    hoverCursor: 'pointer',
  });
  (placeHolder as any).isImagePlaceholder = true;

  canvas.add(placeHolder);
  return placeHolder;
};

const createFileInput = (
  canvas: fabric.Canvas,
  object: TemplateObject,
  selectImageHandler: any
) => {
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = 'image/*';
  fileInput.style.display = 'none';
  document.body.appendChild(fileInput);

  fileInput.addEventListener('change', async (event: any) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        selectImageHandler(e.target.result, object);
        const placeholderObject = canvas.getObjects().find(
          (obj) => (obj as any).isImagePlaceholder && obj.left === object.x && obj.top === object.y
        );

        if (placeholderObject) {
          (placeholderObject as any).isImagePlaceholder = false;
          canvas.remove(placeholderObject);
        }
      };
      if (file.type.match('image.*')) {
        reader.readAsDataURL(file);
      } else {
        console.error('File type not supported');
      }
    }
  });

  return fileInput;
};
